import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import styled from 'styled-components';
import {labels, currentResource, isNullOrUndefined} from '../../../../../../../util/utils';
import {getAccountsForALRAAGGridTreeView} from '../../../../../../../selectors/Ares/accounts';
import AgGridWrapper from '../../../../../BaseComponentWrappers/AgGridWrapper';
import NameAccountCellRenderer from './NameAccountCellRenderer';
import AccountTypeCellRenderer from './AccountTypeCellRenderer';
import RiskAccountCellRenderer from './RiskAccountCellRenderer';
import InherentRiskTypeCellRenderer from './InherentRiskTypeCellRenderer';
import AccountsRisksCellRenderer from './AccountsRisksCellRenderer';
import ALRAAssertionsTableBottom from './ALRAAssertionsTableBottom';
import Tooltip from '../../../../../BaseComponentWrappers/MotifTooltip';
import ALRAColumnChooser from './ALRAColumnChooser';
import { HeaderNameRenderer} from './ALRAHeaderNameRenderer';

export default function ALRAAssertionsTable({selectedStEntityId}) {
	const getDataRow = useSelector((state) => getAccountsForALRAAGGridTreeView(state));
	const containerStyle = useMemo(() => ({width: '100%', height: '100%'}), []);
	const defaultColDef = useMemo(() => {
		return {
			minWidth: 100,
			sortable: false,
			resizable: false,
			suppressMenu: true,
			wrapText: false,
			autoHeight: true,
			suppressMovable: true
		};
	}, []);

	const gridRef = useRef();
	const handleOnGridReady = useCallback((params) => {
		// gridRef.current.api = params.api;
		gridRef.current = {
			api: params.api
		};

		const grids = document.querySelectorAll(`section.AGGridWrapperSection`);

		if (grids?.length > 0) {
			grids.forEach((grid) => {
				grid.querySelector('.ag-body-viewport').classList.add('customScrollbar');
			});
			resizeGrid(params);

			window.addEventListener('resize', function() {
				resizeGrid(params);
			});
		}
	}, []);

	const resizeGrid = (params) => {
		setTimeout(function() {
			params.api.sizeColumnsToFit();
		}, 500);
	};

	const statementTypes = {
		IncomeStatement: 1,
		BalanceSheet: 2,
		Both: 3
	};

	const assertions = currentResource.assertions
		.filter((assertion) => assertion.statementtypeid === statementTypes.Both)
		.sort((a, b) => a.id - b.id);

	const columnDefs = useMemo(
		() => [
			{
				headerName: `${labels.ALRAAccountDisclosure}`,
				cellRenderer: 'accountNameCellRenderer',
				baseColumn: true,
				children: [
					{
						minWidth: window.innerWidth > 1280 ? '598' : '500',
						headerName: '',
						cellRenderer: 'agGroupCellRenderer',
						showRowGroup: true,
						cellRendererParams: {
							suppressCount: true,
							innerRenderer: AccountsRisksCellRenderer
						},
						onCellClicked: (params) => {
							if (
								params.event.target.querySelector('span.account-name') ||
								params.event.target.querySelector('section.StyledCreate') ||
								params.event.target.querySelector('span.motif-tooltip-trigger-wrap')
							) {
								// update the node to be expanded
								params.node.expanded = !params.node.expanded;
								// tell the grid to redraw based on state of nodes expanded state
								params.api.onGroupExpandedOrCollapsed(params.rowIndex);
							} else {
								params.event.stopPropagation();
							}
						},
						colSpan: (params) => {
							let columnCount = 4;
							const displayedColumns = params.api.getAllDisplayedColumns();
							if (displayedColumns?.length < 9) {
								columnCount = 4 - (9 - displayedColumns.length);
							}
							return params.data && params.data.path && params.data.path.length === 2 ? columnCount : 1;
						}
					}
				]
			},
			{
				field: 'accountTypeId',
				headerName: `${labels.ALRADesignation}`,
				children: [
					{
						field: 'accountTypeId',
						headerName: ``,
						cellRenderer: 'accountTypeCellRenderer',
						maxWidth: window.innerWidth < 1280 ? '150' : '200'
					}
				],
				baseColumn: true
			},
			{
				field: 'assertions',
				headerName: `${labels.ALRARisks}`,
				maxWidth: '150',
				children: [
					{
						field: 'assertions',
						headerName: ``,
						cellRenderer: 'riskAccountCellRenderer',
						maxWidth: window.innerWidth < 1280 ? '120' : '150'
					}
				],
				baseColumn: false,
				hide: false
			},
			{
				field: 'priorAccountTypeID',
				headerName: `${labels.ALRAPriorPeriodDesignation}`,
				headerGroupComponent: 'headerNameRenderer',
				headerGroupComponentParams: {name: labels.priorPeriod, date: labels.ALRADesignation.toLowerCase()},
				maxWidth: '200',
				children: [
					{
						field: 'priorAccountTypeID',
						headerName: ``,
						maxWidth: window.innerWidth < 1280 ? '200' : '250',
						cellRenderer: 'accountTypeCellRenderer',
						hide: false
					}
				],
				hide: false,
				baseColumn: false
			},
			{
				field: 'priorHasEstimate',
				headerGroupComponent: 'headerNameRenderer',
				headerName: `${labels.ALRAPriorPeriodEstimate}`,
				headerGroupComponentParams: {name: labels.priorPeriod, date: labels.estimate.toLowerCase()},
				maxWidth: '100',
				children: [
					{
						field: 'priorHasEstimate',
						headerName: ``,
						maxWidth: window.innerWidth < 1280 ? '100' : '150',
						cellRenderer: 'booleanRenderer',
						hide: true
					}
				],
				hide: true,
				baseColumn: false
			},
			{
				field: 'assertions',
				headerName: `${labels.ALRAAssertions}`,
				children: [
					{
						field: 'c',
						headerComponent: 'assertionsColumnHeadersRenderer',
						headerComponentParams: {
							headerName: `${labels.ALRAC}`,
							headerTooltip: `${assertions[0]?.assertionname}`
						},
						maxWidth: window.innerWidth < 1280 ? '60' : '80',
						cellRenderer: 'inherentRiskTypeCellRenderer'
					},
					{
						field: 'eo',
						headerComponent: 'assertionsColumnHeadersRenderer',
						headerComponentParams: {
							headerName: `${labels.ALRAEO}`,
							headerTooltip: `${assertions[1]?.assertionname}`
						},
						maxWidth: window.innerWidth < 1280 ? '60' : '80',
						cellRenderer: 'inherentRiskTypeCellRenderer'
					},
					{
						field: 'mv',
						headerComponent: 'assertionsColumnHeadersRenderer',
						headerComponentParams: {
							headerName: `${labels.ALRAMV}`,
							headerTooltip: `${assertions[2]?.assertionname}`
						},
						maxWidth: window.innerWidth < 1280 ? '60' : '80',
						cellRenderer: 'inherentRiskTypeCellRenderer'
					},
					{
						field: 'ro',
						headerComponent: 'assertionsColumnHeadersRenderer',
						headerComponentParams: {
							headerName: `${labels.ALRARO}`,
							headerTooltip: `${assertions[3]?.assertionname}`
						},
						maxWidth: window.innerWidth < 1280 ? '60' : '80',
						cellRenderer: 'inherentRiskTypeCellRenderer'
					},
					{
						field: 'pd',
						headerComponent: 'assertionsColumnHeadersRenderer',
						headerComponentParams: {
							headerName: `${labels.ALRAPD}`,
							headerTooltip: `${assertions[4]?.assertionname}`
						},
						maxWidth: window.innerWidth < 1280 ? '60' : '80',
						cellRenderer: 'inherentRiskTypeCellRenderer'
					}
				],
				baseColumn: true
			}
		],
		[]
	);

	const noRowsOverlayComponent = () => {
		return <span>{selectedStEntityId > 0 ? labels.StEntityNoRecords : labels.ALRANoRecords}</span>;
	};

	const getDataPath = useMemo(
		() => (data) => {
			const path = data?.path;
			if (path && path.length === 2) {
				if (path[1] === 'NO_RISKS_ASSOCIATED') {
					path[1] = `${labels.ALRANoRisksAssociated}.`;
				}
				if (path[1] === 'LIMITED_RISK_ACCOUNT') {
					path[1] = `${labels.ALRALimitedRiskAccount}.`;
				}
				if (path[1] === 'INSIGNIFICANT_RISK_ACCOUNT') {
					path[1] = `${labels.ALRAInsignificantRiskAccount}.`;
				}
			}
			return path;
		},
		[]
	);

	const groupDisplayType = 'custom';

	return (
		<>
			<StyledALRAAssertionsTable className="StyledALRAAssertionsTable">
				<section className="ColumnChooser">
					<ALRAColumnChooser
						columDefs={columnDefs}
						numberOfColsAllowedToSelect={2}
						label={labels.ALRASelectUpToTwoOptions}
						gridRef={gridRef}
					/>
				</section>
				<section style={containerStyle} className="AGGridWrapperSection">
					<AgGridWrapper
						rowData={getDataRow}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						tooltipShowDelay={500}
						groupDisplayType={groupDisplayType}
						noRowsOverlayComponent={noRowsOverlayComponent}
						suppressCellFocus
						wrapHeaderText
						autoHeaderHeight
						suppressContextMenu
						onReady={handleOnGridReady}
						domLayout="normal"
						getDataPath={getDataPath}
						treeData
						components={{
							nameCellRenderer: NameAccountCellRenderer,
							accountTypeCellRenderer: AccountTypeCellRenderer,
							riskAccountCellRenderer: RiskAccountCellRenderer,
							headerNameRenderer: HeaderNameRenderer,
							inherentRiskTypeCellRenderer: InherentRiskTypeCellRenderer,
							assertionsColumnHeadersRenderer: AssertionsColumnHeadersRenderer,
							booleanRenderer: BooleanRenderer
						}}
					/>
					{getDataRow?.length > 0 && <ALRAAssertionsTableBottom />}
				</section>
			</StyledALRAAssertionsTable>
		</>
	);
}

const StyledALRAAssertionsTable = styled.section`
	position: relative;
	height: 100%;
	.ColumnChooser {
		display: flex;
		flex-direction: row;
		justify-content: flex-end;
		position: absolute;
		right: var(--px-20);
		top: calc(var(--px-60) * -1);
	}
	.AGGridWrapperSection {
		.ag-root-wrapper {
			border-radius: var(--px-8) var(--px-8) 0 0 !important;
		}
		.ag-header {
			.ag-header-row {
				.ag-column-last {
					div {
						justify-content: center;
					}
				}
			}
			.ag-header-viewport {
				.ag-header-container {
					.ag-header-row {
						.ag-header-group-cell {
							font-size: var(--px-14);
							font-weight: 400;
							&[col-id='1_0'] {
								padding-left: var(--px-5) !important;
								padding-right: var(--px-5) !important;
								.ag-header-cell-label {
									justify-content: center !important;
								}
							}
							&[col-id='2_0'] {
								padding-left: var(--px-5) !important;
								padding-right: var(--px-5) !important;
								.ag-header-cell-label {
									justify-content: center !important;
								}
							}
							&[col-id='3_0'] {
								padding-left: var(--px-5) !important;
								padding-right: var(--px-5) !important;
								.ag-header-cell-label {
									justify-content: center !important;
								}
							}
							&[col-id='4_0'] {
								padding-left: var(--px-5) !important;
								padding-right: var(--px-5) !important;
								.ag-header-cell-label {
									justify-content: center !important;
								}
							}
							&[col-id='5_0'] {
								padding-left: var(--px-5) !important;
								padding-right: var(--px-5) !important;
								.ag-header-cell-label {
									justify-content: center !important;
								}
							}
							&[col-id='6_0'] {
								padding-left: var(--px-5) !important;
								padding-right: var(--px-5) !important;
								.ag-header-cell-label {
									justify-content: center !important;
								}
							}
						}
					}
				}
			}
		}
		.ag-body {
			.ag-body-viewport {
				.ag-row {
					&:last-child {
						border-bottom: 0;
					}
					.ag-cell {
						&[col-id='accountTypeId'] {
							padding-left: var(--px-5) !important;
							padding-right: var(--px-5) !important;
						}
						&[col-id='assertions'] {
							padding-left: var(--px-5) !important;
							padding-right: var(--px-5) !important;
						}
					}
				}
			}
		}
	}
`;

const BooleanRenderer = (params) => {
	if (!isNullOrUndefined(params.value)) {
		return <section>{params.value ? labels.yes : labels.no}</section>;
	} else return <section>-</section>;
};

export function AssertionsColumnHeadersRenderer(params) {
	return (
		<StyledAssertionsColumnHeadersRenderer className="StyledAssertionsColumnHeadersRenderer">
			<Tooltip
				placement="top"
				hideCloseButton
				className="assertion-column-header"
				variant="alt"
				trigger={<span>{params.headerName}</span>}
			>
				{params.headerTooltip}
			</Tooltip>
		</StyledAssertionsColumnHeadersRenderer>
	);
}

const StyledAssertionsColumnHeadersRenderer = styled.section`
	display: flex;
	justify-content: center;
	align-items: center;
	height: var(--px-36);
	width: 100%;
	font-weight: normal;
	color: var(--neutrals-900);
`;
